import React from 'react';
import Icon from '../../../components/AppIcon';

const AuditSummary = ({ summaryData }) => {
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-GB')?.format(num);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'created': return 'Plus';
      case 'modified': return 'Edit';
      case 'reviewed': return 'CheckCircle';
      case 'exported': return 'Download';
      case 'deleted': return 'Trash2';
      default: return 'Activity';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'created': return 'text-success bg-success/10';
      case 'modified': return 'text-warning bg-warning/10';
      case 'reviewed': return 'text-primary bg-primary/10';
      case 'exported': return 'text-accent bg-accent/10';
      case 'deleted': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Audit Overview</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">
              {formatNumber(summaryData?.totalEntries)}
            </div>
            <div className="text-sm text-muted-foreground">Total Entries</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-accent">
              {summaryData?.activeUsers}
            </div>
            <div className="text-sm text-muted-foreground">Active Users</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-warning">
              {formatNumber(summaryData?.todayEntries)}
            </div>
            <div className="text-sm text-muted-foreground">Today</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success">
              {formatNumber(summaryData?.weekEntries)}
            </div>
            <div className="text-sm text-muted-foreground">This Week</div>
          </div>
        </div>
      </div>
      {/* Action Breakdown */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Action Breakdown</h3>
        <div className="space-y-3">
          {summaryData?.actionBreakdown?.map((action) => (
            <div key={action?.type} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getActionColor(action?.type)}`}>
                  <Icon name={getActionIcon(action?.type)} size={16} />
                </div>
                <span className="text-sm font-medium text-foreground capitalize">
                  {action?.type}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-foreground font-medium">
                  {formatNumber(action?.count)}
                </span>
                <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${getActionColor(action?.type)?.split(' ')?.[0]?.replace('text-', 'bg-')}`}
                    style={{ width: `${(action?.count / summaryData?.totalEntries) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Most Active Users */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Most Active Users</h3>
        <div className="space-y-3">
          {summaryData?.mostActiveUsers?.map((user, index) => (
            <div key={user?.email} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-primary">
                    {index + 1}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {user?.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {user?.email}
                  </div>
                </div>
              </div>
              <div className="text-sm font-medium text-foreground">
                {formatNumber(user?.activityCount)} actions
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Frequently Changed Fields */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Frequently Changed Fields</h3>
        <div className="space-y-3">
          {summaryData?.frequentFields?.map((field, index) => (
            <div key={field?.name} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-muted rounded flex items-center justify-center">
                  <Icon name="Edit" size={12} className="text-muted-foreground" />
                </div>
                <span className="text-sm font-medium text-foreground">
                  {field?.displayName}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-foreground">
                  {formatNumber(field?.changeCount)}
                </span>
                <div className="w-12 h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary"
                    style={{ 
                      width: `${(field?.changeCount / summaryData?.frequentFields?.[0]?.changeCount) * 100}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Recent Activity Timeline */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h3>
        <div className="space-y-4">
          {summaryData?.recentActivity?.map((activity, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getActionColor(activity?.action)}`}>
                <Icon name={getActionIcon(activity?.action)} size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-foreground">
                  <span className="font-medium">{activity?.user}</span>
                  {' '}
                  <span className="text-muted-foreground">{activity?.action}</span>
                  {' '}
                  <span className="font-mono">{activity?.invoiceReference}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {new Date(activity.timestamp)?.toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AuditSummary;